import { FC, ReactNode } from 'react';
import { Collapse } from 'antd';
import { LucideLeaf, LucidePiggyBank, LucideShapes, Tags as TagsIcon } from 'lucide-react';
import {
    TbBuildingBank,
    TbBuildingFactory2,
    TbCalendarDollar,
    TbChartDonut,
    TbChartPie,
    TbChevronDown,
    TbCrosshair,
    TbFoldUp,
    TbMapPin2,
    TbNumber123,
    TbTagStarred,
    TbUsersGroup,
    TbWorldBolt,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import SearchFilterSectionLabel from '@/shared/components/filters/SearchFilterSectionLabel';
import SearchFilterSectionOverflow from '@/shared/components/filters/SearchFilterSectionOverflow';

import {
    AssetClass,
    Attributes,
    EmployeeHeadcount,
    ESG,
    Exchanges,
    FiscalYearEnd,
    Fundamentals,
    Fundraising,
    HeadquartersLocation,
    IdentifierCodes,
    Industry,
    InstitutionalOwnership,
    Sector,
    TagsFilter,
    WatchlistsFilter,
    WebsiteTraffic,
} from './components';

interface FilterOptionsProps {
    filters: any;
    handleChange: any;
    presentation?: 'collapse' | 'editor';
    selectedSectionKey?: string;
}

interface CompanyFilterSection {
    children: ReactNode;
    icon: ReactNode;
    key: string;
    label: string;
}

const getCompanyFilterSections = (filters: any, handleChange: any): CompanyFilterSection[] => [
    {
        key: 'workspaceTags',
        icon: <TagsIcon size='1rem' />,
        label: 'Tags',
        children: <TagsFilter filters={filters} handleChange={handleChange} />,
    },
    {
        key: 'watchlists',
        icon: <TbCrosshair size='1rem' />,
        label: 'Watchlists',
        children: <WatchlistsFilter filters={filters} handleChange={handleChange} />,
    },
    {
        key: 'assetClasses',
        icon: <LucideShapes size='1rem' />,
        label: 'Asset Type',
        children: (
            <>
                <AssetClass filters={filters} handleChange={handleChange} />
            </>
        ),
    },
    // {
    //     key: 'securityType',
    //     header: (
    //         <>
    //             <TbCertificate size="1rem" style={{ marginRight: '0.375rem', verticalAlign: '-0.175rem' }} />
    //             <span>Security Type</span>
    //         </>
    //     ),
    //     children: (
    //         <>
    //             <SecurityType />
    //         </>
    //     ),
    // },
    {
        key: 'exchanges',
        icon: <TbBuildingBank size='1rem' />,
        label: 'Exchanges',
        children: (
            <>
                <Exchanges filters={filters} handleChange={handleChange} />
            </>
        ),
    },
    {
        key: 'identifierCodes',
        icon: <TbNumber123 size='1rem' />,
        label: 'Identifier Codes',
        children: (
            <>
                <IdentifierCodes filters={filters} handleChange={handleChange} />
            </>
        ),
    },
    {
        key: 'industries',
        icon: <TbBuildingFactory2 size='1rem' />,
        label: 'Industries',
        children: (
            <>
                <Industry filters={filters} handleChange={handleChange} />
            </>
        ),
    },
    {
        key: 'sectors',
        icon: <TbChartPie size='1rem' />,
        label: 'Sectors',
        children: (
            <>
                <Sector filters={filters} handleChange={handleChange} />
            </>
        ),
    },
    {
        key: 'headquartersLocation',
        icon: <TbMapPin2 size='1rem' />,
        label: 'Locations (HQ)',
        children: (
            <>
                <HeadquartersLocation filters={filters} handleChange={handleChange} />
            </>
        ),
    },
    {
        key: 'fundamentals',
        icon: <TbFoldUp size='1rem' />,
        label: 'Fundamentals',
        children: (
            <>
                <Fundamentals filters={filters} handleChange={handleChange} />
            </>
        ),
    },
    {
        key: 'fiscalYearEnd',
        icon: <TbCalendarDollar size='1rem' />,
        label: 'Fiscal Year-End',
        children: (
            <>
                <FiscalYearEnd filters={filters} handleChange={handleChange} />
            </>
        ),
    },
    {
        key: 'employeeHeadcount',
        icon: <TbUsersGroup size='1rem' />,
        label: 'Employee Headcount',
        children: (
            <>
                <EmployeeHeadcount filters={filters} handleChange={handleChange} />
            </>
        ),
    },
    {
        key: 'institutionalOwnership',
        icon: <TbChartDonut size='1rem' />,
        label: 'Institutional Ownership',
        children: (
            <>
                <InstitutionalOwnership filters={filters} handleChange={handleChange} />
            </>
        ),
    },
    {
        key: 'companyTags',
        icon: <TbTagStarred size='1rem' />,
        label: 'Attributes',
        children: (
            <>
                <Attributes filters={filters} handleChange={handleChange} />
            </>
        ),
    },
    {
        key: 'esg',
        icon: <LucideLeaf size='1rem' />,
        label: 'ESG',
        children: (
            <>
                <ESG filters={filters} handleChange={handleChange} />
            </>
        ),
    },
    // {
    //     key: 'technologies',
    //     header: (
    //         <>
    //             <TbCloudBolt size='1rem' style={{ marginRight: '0.375rem', verticalAlign: '-0.175rem' }} />
    //             <span>Technologies</span>
    //         </>
    //     ),
    //     children: (
    //         <>
    //             <Technologies filters={filters} handleChange={handleChange} />
    //         </>
    //     ),
    // },
    {
        key: 'websiteTraffic',
        icon: <TbWorldBolt size='1rem' />,
        label: 'Website Traffic',
        children: (
            <>
                <WebsiteTraffic filters={filters} handleChange={handleChange} />
            </>
        ),
    },
    {
        key: 'fundraising',
        icon: <LucidePiggyBank size='1rem' />,
        label: 'Fundraising',
        children: (
            <>
                <Fundraising filters={filters} handleChange={handleChange} />
            </>
        ),
    },
];

const FilterOptions: FC<FilterOptionsProps> = ({
    filters,
    handleChange,
    presentation = 'collapse',
    selectedSectionKey,
}): JSX.Element => {
    const attributeFilters = getCompanyFilterSections(filters, handleChange);

    const { token } = useAntDesignTheme();

    if (presentation === 'editor') {
        return <>{attributeFilters.find((section) => section.key === selectedSectionKey)?.children}</>;
    }

    return (
        <>
            <div>
                <small style={{ color: token.colorTextTertiary }}>Company Filters ({attributeFilters?.length})</small>
            </div>
            <Collapse
                onChange={() => {}}
                expandIconPosition={'end'}
                style={{
                    backgroundColor: token.colorBgBase,
                    borderColor: token.colorBorder,
                    margin: '0.75rem 0',
                }}
                expandIcon={(panelProps) => {
                    return (
                        <TbChevronDown
                            size={'1rem'}
                            style={{
                                transform: `rotate(${panelProps.isActive ? '0deg' : '-90deg'})`,
                                color: token.colorTextTertiary,
                            }}
                        />
                    );
                }}
            >
                {attributeFilters.map((sf) => (
                    <Collapse.Panel
                        key={sf?.key}
                        header={
                            <>
                                <SearchFilterSectionLabel icon={sf.icon} label={sf.label} />
                                {/* {filters[sf?.key] && (
                                    <SmallTag
                                        message={filters[sf?.key]?.length || Object.values(filters[sf?.key])?.length}
                                    />
                                )} */}
                            </>
                        }
                        style={{ borderColor: token.colorBorder }}
                    >
                        <SearchFilterSectionOverflow>{sf?.children}</SearchFilterSectionOverflow>
                    </Collapse.Panel>
                ))}
            </Collapse>
        </>
    );
};

export default FilterOptions;
